import { useAppDispatch } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useUser } from '@/app/redux/hooks/user';
import { Invitation } from '@/common/types/organisation';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import ensureError from '@/common/utils/error';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { respondToInviteThunk } from '@/dashboard/dashboardThunks';
import api from '@/db/apiClient';
import { CheckIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  SettingsContentContainer,
  SettingsHeaderContainer,
  SettingsHeaderText,
  SettingsPageContainer,
  SettingsSectionHeader,
} from './SettingsPageStyles';

export default function MyOrganisations() {
  const dispatch = useAppDispatch();

  const { currentOrganisationId, allOrganisations, switchOrganisation } =
    useOrganisation();
  const { user } = useUser();
  const userEmail = user?.email;

  const sortedOrganisations = [...allOrganisations].sort((a, b) => {
    if (a.id === currentOrganisationId) return -1;
    else if (b.id === currentOrganisationId) return 1;
    else return 0;
  });

  const [isLoading, setLoading] = useState(true);
  const [acceptingInviteToken, setAcceptingInviteToken] = useState<
    string | null
  >(null);
  const [rejectingInviteToken, setRejectingInviteToken] = useState<
    string | null
  >(null);
  const [pendingInvites, setPendingInvites] = useState<Invitation[]>([]);

  useEffect(() => {
    async function fetchPendingInvites() {
      try {
        if (!userEmail) return;
        const invites =
          await api.asEducator.getPendingInvitesForEmail(userEmail);
        setPendingInvites(invites);
      } catch (error) {
        console.error('Error fetching pending invites:', error);
      } finally {
        setLoading(false);
      }
    }

    setLoading(true);
    fetchPendingInvites();
  }, [userEmail]);

  async function handleAcceptInvite(invite: Invitation) {
    if (!user) {
      console.error('User is missing');
      toast.error('Failed to respond to invite', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      setAcceptingInviteToken(invite.token);
      await dispatch(respondToInviteThunk(invite, true, switchOrganisation));
      toast.success('Invite accepted');
      trackMixpanelEvent('Invite Accepted', {
        organisationId: invite.organisationId,
        referrer: invite.inviterEmail,
      });
      setPendingInvites((invites) =>
        invites.filter((i) => i.token !== invite.token),
      );
    } catch (e) {
      const error = ensureError(e);
      console.error('Error accepting invite:', error);
      toast.error('Failed to accept invite', {
        description: error.message || UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setAcceptingInviteToken(null);
    }
  }

  async function handleRejectInvite(invite: Invitation) {
    if (!user) {
      console.error('User is missing');
      toast.error('Failed to respond to invite', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      setRejectingInviteToken(invite.token);
      await dispatch(respondToInviteThunk(invite, false, switchOrganisation));
      toast.success('Invite rejected');
      trackMixpanelEvent('Invite Rejected', {
        organisationId: invite.organisationId,
        referrer: invite.inviterEmail,
      });
      setPendingInvites((invites) =>
        invites.filter((i) => i.token !== invite.token),
      );
    } catch (e) {
      const error = ensureError(e);
      console.error('Error rejecting invite:', error);
      toast.error('Failed to reject invite', {
        description: error.message || UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setRejectingInviteToken(null);
    }
  }

  return (
    <SettingsPageContainer>
      <SettingsHeaderContainer>
        <SettingsHeaderText>
          <h2>My Organisations</h2>
          <div>View and manage your organisations and pending invites</div>
        </SettingsHeaderText>
      </SettingsHeaderContainer>

      <SettingsContentContainer>
        <div>
          <SettingsSectionHeader>Organisations</SettingsSectionHeader>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-6 w-6 rounded-full shrink-0" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-6 w-16 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : allOrganisations.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="text-center py-4 text-muted-foreground"
                  >
                    No organisations
                  </TableCell>
                </TableRow>
              ) : (
                sortedOrganisations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <InitialsCircle userName={org.name} size={24} />
                      {org.name}
                    </TableCell>
                    <TableCell className="text-right">
                      {org.id === currentOrganisationId ? (
                        <Badge variant="default" className="ml-auto w-fit">
                          <CheckIcon className="mr-1 h-3 w-3" />
                          Current
                        </Badge>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => switchOrganisation(org.id)}
                          disabled={
                            !!acceptingInviteToken || !!rejectingInviteToken
                          }
                        >
                          Switch
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Separator />

        <div>
          <SettingsSectionHeader>Pending Invites</SettingsSectionHeader>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organisation</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-6 w-6 rounded-full shrink-0" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Skeleton className="h-8 w-16" />
                        <Skeleton className="h-8 w-16" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : pendingInvites.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="text-center py-4 text-muted-foreground"
                  >
                    No pending invites
                  </TableCell>
                </TableRow>
              ) : (
                pendingInvites.map((invite) => (
                  <TableRow key={invite.token}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <InitialsCircle
                          userName={invite.organisationName}
                          size={24}
                        />
                        {invite.organisationName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          onClick={() => handleAcceptInvite(invite)}
                          size="sm"
                          disabled={
                            !!acceptingInviteToken || !!rejectingInviteToken
                          }
                          loading={acceptingInviteToken === invite.token}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRejectInvite(invite)}
                          disabled={
                            !!acceptingInviteToken || !!rejectingInviteToken
                          }
                          loading={rejectingInviteToken === invite.token}
                        >
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </SettingsContentContainer>
    </SettingsPageContainer>
  );
}
